/**
 * Cascas dos diálogos de item do cadastro do posto: adicionar/editar e confirmar remoção.
 * O item é gravado na hora pelo subrecurso; o erro do servidor aparece no diálogo e o digitado fica.
 */
import React, { useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

interface ItemDialogProps {
  title: string;
  submitLabel: string;
  submitting: boolean;
  /** Erro do servidor que não cita nenhum campo; o que cita fica junto do campo, no filho. */
  error: string | null;
  onSubmit: () => void;
  onClose: () => void;
  children: React.ReactNode;
}

export const ItemDialog: React.FC<ItemDialogProps> = ({
  title,
  submitLabel,
  submitting,
  error,
  onSubmit,
  onClose,
  children,
}) => (
  <Dialog open onClose={submitting ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="item-dialog-title">
    <DialogTitle id="item-dialog-title">{title}</DialogTitle>
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ fontSize: '1rem', mb: 2 }}>
            {error}
          </Alert>
        )}
        {children}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="text" onClick={onClose} disabled={submitting} sx={{ minHeight: 44 }}>
          Descartar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : undefined}
          sx={{ minHeight: 44 }}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </form>
  </Dialog>
);

interface ConfirmDialogProps {
  title: string;
  message: string;
  /** Rótulo do botão que segue em frente; o padrão é o da remoção de item. */
  confirmLabel?: string;
  /** Rótulo do botão que desiste, o único com destaque (a saída segura). */
  cancelLabel?: string;
  /** Pode devolver uma promessa: enquanto ela não termina, um segundo clique em confirmar não faz nada. */
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmLabel = 'Remover',
  cancelLabel = 'Manter',
  onConfirm,
  onCancel,
}) => {
  // Trava síncrona (ref) e não só o estado: dois cliques no mesmo tick leem o estado antigo.
  const sending = useRef(false);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onCancel} aria-labelledby="confirm-dialog-title">
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: '1rem' }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="contained" onClick={onCancel} disabled={busy} sx={{ minHeight: 44 }} autoFocus>
          {cancelLabel}
        </Button>
        <Button variant="outlined" color="error" onClick={confirm} disabled={busy} sx={{ minHeight: 44 }}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
